/** @file RigSection: the selected part's rig by numbers: anchor, offset, tile size, rotate and cut. */
import type { DrawableFields } from '@zilo/pose3d-commands/types/puppetInputs';
import type { FacetEntry } from '@zilo/pose3d-format/types/facet';
import type { Drawable } from '@zilo/pose3d-format/types/drawable';
import type { ReactElement } from 'react';
import type { QuadOverride } from '../../state/editorTypes';
import { useEditor } from '../../state/useEditor';
import type { ResizeEnv } from '../art/fit/applyEntryResize';
import { InspectorSection } from './InspectorSection';
import { RigEntryFields } from './RigEntryFields';
import { VecInput } from './VecInput';

const XYZ = ['X', 'Y', 'Z'] as const;

/** The override the fields show live: a handle drag on this drawable, or a typed value whose batch is still out. */
function liveOf(o: QuadOverride | null, id: string): QuadOverride | null {
  return o !== null && o.drawable === id && (o.by === 'drag' || o.until === null) ? o : null;
}

/** The entry shown: the live override's, else `entry`, clamped to the drawable's entries (none: no facet entries). */
function entryOf(d: Drawable, o: QuadOverride | null, entry: number): { index: number; e: FacetEntry | undefined; live: QuadOverride | null } {
  const facets = d.kind === 'impostor' || d.kind === 'effect' ? (d.facets ?? []) : [];
  const index = Math.max(0, Math.min(o?.entry ?? entry, facets.length - 1));
  return { index, e: facets[index], live: o !== null && o.entry === index ? o : null };
}

/**
 * Drawable `id`'s block: for a part with facet entries the entry `entry` (the inspector's picked one; a running handle
 * drag's own entry while it runs) as `RigEntryFields` (Anchor, Offset, Size, Rotate, Cut); Offset (unit) is one
 * `drawable.set { offset }`. `env` is the resize path (the store, the upload call, the fit prompt).
 */
export function RigSection({ id, entry, env }: { id: string; entry: number; env: ResizeEnv }): ReactElement | null {
  const d = useEditor((s) => s.puppet.doc?.puppet.drawables.find((x) => x.id === id));
  const parent = useEditor((s) => (d === undefined ? undefined : s.puppet.doc?.puppet.nodes[d.node]?.parent));
  const o = liveOf(useEditor((s) => s.quadOverride), id);
  if (d === undefined) return null;
  const { index, e, live } = entryOf(d, o, entry);
  const set = (fields: DrawableFields): void => {
    void env.store.getState().dispatch([{ name: 'drawable.set', input: { drawable: id, fields } }], `set ${id} ${Object.keys(fields).join(', ')}`);
  };
  const aside = e === undefined ? `${d.kind} on ${d.node}` : `entry ${String(index)}${live?.by === 'drag' ? ', dragging' : ''}`;
  const offset = <VecInput label="Offset" unit="unit" axes={XYZ} value={d.offset} step={0.01} onCommit={(next) => { set({ offset: next }); }} />;
  return (
    <InspectorSection title={`Rig ${id}`} aside={aside}>
      {e === undefined ? offset : <RigEntryFields id={id} index={index} e={e} node={d.node} parent={parent} live={live} env={env}>{offset}</RigEntryFields>}
    </InspectorSection>
  );
}
